import * as moment from "moment-timezone";
import * as React from "react";
import styled, { css } from "styled-components";
import * as yup from "yup";
import { toast } from "react-toastify";
import * as _ from "lodash";
import { useHistory } from "react-router-dom";

import { useWindowSize } from "src/library/hooks/useWindowSize";
import { Button } from "src/library/components/Button";
import { DayRangePicker } from "src/library/components/DayRangePicker";
import { Icon } from "src/library/components/Icon";
import { Text } from "src/library/components/Text";
import {
  FormRow,
  Label,
  Radio
} from "src/library/components/Forms";
import Input from "src/library/components/Forms/Input";
import { Icons } from "src/library/components/Icon/types";
import { ICustomer } from "src/core/customers/api/customers.api";
import {
  CustomerBranchFilters,
  CustomerBranchFiltersModal
} from "src/library/components/CustomerBranchFilter";
import {
  CustomerBranchFilterDefault,
  CustomerBranchFilterType
} from "src/core/products/api/products.api";
import { CustomerBranchFilterAction } from "src/library/components/CustomerBranchFilter/CustomerBranchFilters";
import useTerminology from "src/core/shared/hooks/useTerminology";
import useCache from "src/core/shared/hooks/useCache";
import { GLOBAL_SETTINGS_CACHE_KEY } from "src/core/shared/api/globalSettings.api";

import {
  useOpenDaysApi,
  IOpenDay,
  CreateOpenDayFilterAttributes
} from "../api/open-day.api";

interface IOpenDayFormProps {
  initialOpenDay: IOpenDay | null;
}

const OpenDayForm: React.ReactFCWithChildren<IOpenDayFormProps> = props => {
  const { initialOpenDay } = props;
  const history = useHistory();
  const { removeItemByKey } = useCache();
  const terminology = useTerminology();
  const [ createOpenDayRes, createOpenDayReq ] = useOpenDaysApi("CREATE_OPEN_DAY");
  const [ updateOpenDayRes, updateOpenDayReq ] = useOpenDaysApi("UPDATE_OPEN_DAY");
  const { width } = useWindowSize();
  const [ showFiltersModal, setShowFiltersModal ] = React.useState(false);
  const [ selectedCustomer, setSelectedCustomer ] = React.useState<ICustomer | null>(null);

  const [ validation, setValidation ] = React.useState<{ error: boolean; message: string }>({
    error: false,
    message: ""
  });

  const dateFormat = width && width < 786 ? "DD/MM/YY" : "ddd DD MMM YYYY";

  const [ form, setForm ] = React.useState<Partial<IOpenDay>>(initialOpenDay ? {
    ...initialOpenDay,
    startDate: moment.utc(initialOpenDay.startDate).toDate(),
    endDate: moment.utc(initialOpenDay.endDate).toDate()
  } : {});

  // handles the form events to set the values
  const handleFormChange = React.useCallback(
    (name: keyof IOpenDay, value: any, callback?: (f: typeof form) => void) => {
      setValidation({
        error: false,
        message: ""
      });

      const newForm = {
        ...form,
        [ name ]: value
      };

      setForm(newForm);

      if (callback) {
        callback(newForm);
      }
    },
    [ form ]
  );

  // performs the relevant api request based on id
  const saveOpenDay = React.useCallback(() => {
    if (form.id) {
      updateOpenDayReq({
        openDayId: form.id,
        openDayUpdate: {
          ...form,
          filters: _.map(form.filters, f => _.omit(f, [ "customer", "branch" ]))
        }
      });
    } else {
      createOpenDayReq(form as IOpenDay);
    }
  }, [
    form,
    createOpenDayReq,
    updateOpenDayReq
  ]);

  // handles the submit button event
  const handleSubmit = React.useCallback(() => {
    openDaySchema
      .validate(form)
      .then(() => {
        saveOpenDay();
      })
      .catch(err => {
        setValidation({
          error: true,
          message: err.errors
        });
      });
  }, [ form, saveOpenDay ]);

  React.useEffect(() => {
    if (createOpenDayRes.data || updateOpenDayRes.data) {
      removeItemByKey(GLOBAL_SETTINGS_CACHE_KEY);
      history.push("/platform/open-days");
    }

    if (createOpenDayRes.error || updateOpenDayRes.error) {
      toast.error((createOpenDayRes.error ? createOpenDayRes.error.message : null) || (updateOpenDayRes.error ? updateOpenDayRes.error.message : "Error"));
    }
  }, [
    createOpenDayRes,
    history,
    updateOpenDayRes,
    removeItemByKey
  ]);

  // Remove a filter from the array
  const removeFilter = React.useCallback((filter: CreateOpenDayFilterAttributes) => {
    const newFilters = _.remove([ ...form.filters || [] ], f => {
      if (filter.customerId) {
        return f.customerId !== filter.customerId;
      }

      if (filter.branchId) {
        return (f.branchId !== filter.branchId);
      }

      return true;
    });

    handleFormChange("filters", [ ...newFilters ]);
  }, [ form.filters, handleFormChange ]);

  const filterType = React.useMemo(() => {
    if (form.type === CustomerBranchFilterDefault.AllowAll) {
      if (selectedCustomer) {
        return CustomerBranchFilterType.Include;
      } else {
        return CustomerBranchFilterType.Exclude;
      }
    } else {
      if (selectedCustomer) {
        return CustomerBranchFilterType.Exclude;
      } else {
        return CustomerBranchFilterType.Include;
      }
    }
  }, [ form.type, selectedCustomer ]);

  return (
    <FormContainer>
      {validation.error && (
        <ValidationContainer>
          <Icon
            $fillColor="error"
            icon={Icons.ERRORCIRCLE}
          />

          {validation.message}
        </ValidationContainer>
      )}

      <FormRow>
        <Input
          as="input"
          handleChange={e => handleFormChange("reason", e.target.value)}
          name="reason"
          label="Reason for Open Day"
          inputType="text"
          defaultValue={initialOpenDay ? initialOpenDay.reason : ""}
        />
      </FormRow>

      <FormRow>
        <Label
          label="By default, should this open day be open to all customers or open only to the selected customers?"
          for="type"
        />

        <Radio
          disabled={!!form.id}
          options={[
            {
              text: "Allow all to order on this day(s)",
              value: CustomerBranchFilterDefault.AllowAll,
              checked: form.type === CustomerBranchFilterDefault.AllowAll
            },
            {
              text: `Only open this day for selected ${terminology?.customer.plural.text} and ${terminology?.drop.plural.text}`,
              value: CustomerBranchFilterDefault.AllowNone,
              checked: form.type === CustomerBranchFilterDefault.AllowNone
            }
          ]}
          handleChange={e => {
            handleFormChange("type", e.target.value, form => {
              setForm({
                ...form,
                filters: []
              });
            });
          }}
          name="type"
          label=""
          value={form.type}
        />
      </FormRow>

      <FormRow style={{ position: "relative" }}>
        <Label
          label="Select start and end date"
          for="date-range"
        />

        <SelectedDates>
          <Icon
            size={18}
            icon={Icons.CALENDAR}
            $fillColor="midGrey"
          />

          <Text>
            {form && form.startDate && form.endDate ?
              `${moment.utc(form.startDate).format(dateFormat)} - ${moment.utc(form.endDate).format(dateFormat)}` :
              "Dates"}
          </Text>
        </SelectedDates>

        <DayRangePickerWrapper>
          <DayRangePicker
            maxDate={moment.utc().add(2, "years").toDate()}
            date={(form && form.startDate) || new Date()}
            minDate={new Date()}
            initialDateRange={initialOpenDay ? {
              from: moment.utc(initialOpenDay.startDate).startOf("day").toDate(),
              to: moment.utc(initialOpenDay.endDate).endOf("day").toDate()
            } : null}
            handleDateChange={({ from, to }) => {
              if (from) {
                handleFormChange("startDate", from);
              }

              if (to) {
                handleFormChange("endDate", to);
              }
            }}
          />
        </DayRangePickerWrapper>

      </FormRow>

      <CustomerBranchFiltersModal
        existingFilters={form.filters || []}
        isOpen={showFiltersModal}
        onDismiss={() => {
          setShowFiltersModal(false);
          setSelectedCustomer(null);
        }}
        handleSave={data => handleFormChange("filters", form.filters ? [ ...form.filters, ...data ] : data)}
        filterType={filterType}
        customer={selectedCustomer}
      />

      <CustomerBranchFilters
        form={form}
        setSelectedCustomer={setSelectedCustomer}
        setShowFiltersModal={setShowFiltersModal}
        removeFilter={removeFilter}
        action={CustomerBranchFilterAction.OpenDay}
      />

      <SubmitContainer>
        <Button
          loading={createOpenDayRes.pending || updateOpenDayRes.pending}
          $buttonType="primary"
          onClick={() => handleSubmit()}
        >
          <>
            {initialOpenDay ? "Update Open Day" : "Create Open Day"}
          </>
        </Button>
      </SubmitContainer>
    </FormContainer>
  );
};

const ValidationContainer = styled.div`
  ${({ theme }) => css`
    padding: ${theme.spacing.padding.md} 0;
    color: ${theme.colors.error};
    display: flex;
    align-items: center;

    svg {
      margin-right: ${theme.spacing.padding.sm};
    }
  `}
`;

const DayRangePickerWrapper = styled.div`
 .DayPicker {
  width: 225px;
 }
`;

const SubmitContainer = styled.div`

  ${({ theme }) => css`
    padding-top: ${theme.spacing.padding.md};
    > * {
      margin: 0 ${theme.spacing.padding.xs};
    }

    > button:first-child {
      margin: 0 -${theme.spacing.padding.xs};
    }
  `}
`;

const FormLine = styled.div`
  ${({ theme }) => css`
    padding: ${theme.spacing.padding.md} 0;
  `}
`;

const FormContainer = styled.div`
  &.suspended ${FormLine} {
    opacity: 0.5;
    pointer-events: none;
  }
`;

const SelectedDates = styled.div`
  display: flex;
  flex-direction: row;
  > span {
    ${({ theme }) => css`
      margin-left: ${theme.spacing.padding.xs};
    `}
  }
`;

export const openDaySchema = yup.object().shape({
  reason: yup.string().required(),
  type: yup.string().oneOf([ CustomerBranchFilterDefault.AllowAll, CustomerBranchFilterDefault.AllowNone ]).required(),
  startDate: yup
    .date()
    .label("Start date")
    .required(),
  endDate: yup
    .date()
    .label("End date")
    .required()
    .when("startDate", (startDate: Date, schema: yup.DateSchema) => {
      if (startDate) {
        return schema.min(startDate);
      } else {
        return schema;
      }
    })
});

export default OpenDayForm;
